import { Component, Input, SimpleChanges, OnInit, OnChanges, OnDestroy } from '@angular/core';
import { MasterService } from 'src/app/services/master.service';
import { MessageService } from 'src/app/services/message.service';
import * as languageLibrary from '../../../../../../services/language'
import { FormBuilder, FormArray, Validators } from '@angular/forms';
import { DomSanitizer } from '@angular/platform-browser';
import { SaleProductMeta } from 'src/app/models/sale-product-meta.model';
import { BackEndCommission } from 'src/app/models/backend-commission.model';
import { Employee } from 'src/app/models/employee.model';
import { Subscription } from 'rxjs';

@Component({
    selector: 'sale-commission',
    templateUrl: './sale-commission.component.html',
    styleUrl: './sale-commission.component.scss',
    standalone: false
})

export class SaleCommissionComponent implements OnInit, OnChanges, OnDestroy {

    @Input() saleId: number = 0
    
    protected initialDown: number = 0

    protected products: SaleProductMeta;
    
    protected employees: Employee[] = []
    
    protected commission: {} = {}
    
    protected language: string = localStorage.getItem('language') ? localStorage.getItem('language') : 'EN'
    
    protected words = languageLibrary.language
    
    protected form = null
    
    protected frontEndCommission: number = 0;
    
    protected backEndCommission: BackEndCommission = new BackEndCommission();
    
    protected selectableEmployeesFrontEnd: never[];
    
    protected selectedEmployee = null
    
    protected totalFECommission = 0
    
    protected totalBECommission = 0

    /** Listen for component events and make decisions. */
    private componentWatcher: Subscription | null = null
    
    public pdf: any = null

    get frontEndList() {
        return this.form.controls['frontEnd'] as FormArray;
    }
    get backEndList() {
        return this.form.controls['backEnd'] as FormArray;
    }

    constructor(private master: MasterService, private ms: MessageService, private fb: FormBuilder, private sanitizer: DomSanitizer) {
        this.form = this.fb.group({
            frontEnd: this.fb.array([]),
            backEnd: this.fb.array([])
        })
    }

    ngOnInit() {
        this.fetchEmployees()

        this.componentWatcher = this.ms.channelComponents$.subscribe((event) => {
            if (!event || !event.message) {
                return
            }

            switch (event.message) {
                case 'productsRead':
                case 'productsChanged':
                    this.products = event.value.value
                    break
                case 'downsRead':
                case 'downsChanged':
                    this.initialDown = event.value.initial || 0
                    break
            }
        })
    }

    ngOnDestroy() {
        if (this.componentWatcher) {
            this.componentWatcher.unsubscribe()
        }
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (!changes) {
            return
        }
        
        if (changes.saleId) {
            this.fetch()
        }

        if (changes.initialDown) {
            this.initialDown = parseFloat(changes.initialDown.currentValue)
        }

        this.frontEndCommission = this.initialDown
        this.calculateBackendProfit()
    }

    public calculateBackendProfit() {
        if (this.products && this.products.productCount > 0) {
            this.backEndCommission = { total: 0, products: {} }
            this.products.products.forEach(product => {
                const profit = product.sellingPrice - product.dealerCost
                if (!this.backEndCommission.products[`${product.productName}`]) {
                    this.backEndCommission.products[`${product.productName}`] = 0
                }

                this.backEndCommission.products[`${product.productName}`] += profit
                this.backEndCommission.total += profit
            });
        }
    }

    public addSalesPerson(section) {
        let salesPerson = this.fb.group({
            id: [null],
            employeeContactId: [{ value: 0, disabled: false }, []],
            saleId: [this.saleId, [Validators.required]],
            frontEndCommission: [section == "frontEnd", []],
            backEndCommission: [section == "backEnd", []],
            percentage: [{ value: 0, disabled: true }, []],
            flat: [{ value: 0, disabled: true }, []],
            overRideAmount: [{ value: 0, disabled: false }, []],
            actualAmount: [{ value: 0, disabled: true }, []],
            calculatedAmount: [{ value: 0, disabled: true }, []],
        },)

        section == "frontEnd" ? this.frontEndList.push(salesPerson) : this.backEndList.push(salesPerson)
    }

    public insertSalesPerson(salesCommission) {
        this.frontEndList.clear()
        this.backEndList.clear()
        this.totalFECommission = 0
        this.totalBECommission = 0
        salesCommission.forEach(salesperson => {
            let salesPerson = this.fb.group({
                id: [salesperson['id'] || null],
                employeeContactId: [{ value: salesperson['employeeContactId'], disabled: false }, []],
                saleId: [this.saleId, [Validators.required]],
                frontEndCommission: [salesperson['frontEndCommission'], []],
                backEndCommission: [salesperson['backEndCommission'], []],
                percentage: [{ value: salesperson['percentage'], disabled: true }, []],
                flat: [{ value: salesperson['flat'], disabled: true }, []],
                overRideAmount: [{ value: salesperson['overRideAmount'], disabled: false }, []],
                actualAmount: [{ value: salesperson['actualAmount'], disabled: true }, []],
                calculatedAmount: [{ value: salesperson['calculatedAmount'], disabled: true }, []],
            },)

            if (salesperson['frontEndCommission'] == true) {
                this.frontEndList.push(salesPerson)
                this.totalFECommission = this.totalFECommission + parseFloat(salesperson['actualAmount'])
            } else {
                this.backEndList.push(salesPerson)
                this.totalBECommission = this.totalBECommission + parseFloat(salesperson['actualAmount'])
            }

        });
    }

    private fetchEmployees() {
        this.master.get(`employees`, res => {
            if (!res) {
                this.ms.sendMessage("alert", { type: "danger", text: this.words[this.language]['apiNoResponse'] })
                return
            }

            if (res.status !== 200) {
                this.ms.sendMessage("alert", { type: "danger", text: res.data.error })
                return
            }

            this.employees = res.data.employees
        })
    }

    private fetch() {
        const identity = this.saleId
        if (isNaN(identity) || (identity <= 0)) {
            return
        }
        this.master.get(`sales/${this.saleId}/commission`, res => {
            if (!res) {
                this.ms.sendMessage("alert", { type: "danger", text: this.words[this.language]['apiNoResponse'] })
                return
            }

            if (res.status !== 200) {
                this.ms.sendMessage("alert", { type: "danger", text: res.data.error })
                return
            }

            this.insertSalesPerson(res.data.salesCommission)
            // this.selectableEmployeesFrontEnd = this.employees.filter((product) => !this.frontEndList.controls.find(item => item['value']['productName'] === product['name']));
        })
    }

    public discard(id, index, section) {
        if (id.value) {
            this.deleteSalesCommission(id.value)
        }
        section == 'frontEnd' ? this.frontEndList.removeAt(index) : this.backEndList.removeAt(index)
    }

    public deleteSalesCommission(id) {
        const identity = this.saleId
        if (isNaN(identity) || (identity <= 0)) {
            return
        }

        this.master.discard(`sales/${this.saleId}/commission/${id}`, res => {
            if (!res) {
                this.ms.sendMessage("alert", { type: "danger", text: this.words[this.language]['apiNoResponse'] })
                return
            }

            if (res.status !== 200) {
                this.ms.sendMessage("alert", { type: "danger", text: res.data.error })
                return
            }

            this.computeCommission()
        })
    }

    public checkForDuplicatesEmployee(salesPeople) {
        return new Set(salesPeople).size > this.employees.length
    }

    public computeCommission() {
        const identity = this.saleId
        if (isNaN(identity) || (identity <= 0)) {
            return
        }

        if (this.checkForDuplicatesEmployee(this.frontEndList.value)) {
            alert("This employee is already selected for front-end commission. Select a different employee, or cancel.")
            return
        }

        let information = this.form.getRawValue()
        this.master.post(`sales/${this.saleId}/commission`, { information, backEndCommission: this.backEndCommission.total, frontEndCommission: this.frontEndCommission }, res => {
            if (!res) {
                this.ms.sendMessage("alert", { type: "danger", text: this.words[this.language]['apiNoResponse'] })
                return
            }

            if (res.status !== 200) {
                this.ms.sendMessage("alert", { type: "danger", text: res.data.error })
                return
            }

            this.fetch()
        })
    }

    setCommission(commission: {}) {
        let frontEndArr = commission['profits']['frontEnd']['salesPeople']
        let backEndArr = commission['profits']['backEnd']['salesPeople']
        for (let index = 0; index < frontEndArr.length; index++) {
            const salesPerson = frontEndArr[index];
            const i = this.frontEndList.controls.findIndex(res => res.get('employeeContactId').value == salesPerson['employeeContactId'])
            this.frontEndList.controls[i].patchValue({
                id: salesPerson.id || 0,
                frontEndCommission: salesPerson.frontEndCommission || 0,
                backEndCommission: salesPerson.backEndCommission || false,
                percentage: salesPerson.percentage || 0,
                flat: salesPerson.flat || 0,
                overRideAmount: salesPerson.overRideAmount || 0,
                actualAmount: salesPerson.actualAmount || 0,
                calculatedAmount: salesPerson.actualAmount || 0,
            })
        }
        for (let index = 0; index < backEndArr.length; index++) {
            const salesPerson = backEndArr[index];
            const i = this.backEndList.controls.findIndex(res => res.get('employeeContactId').value == salesPerson['employeeContactId'])
            this.backEndList.controls[i].patchValue({
                id: salesPerson.id || 0,
                frontEndCommission: salesPerson.frontEndCommission || 0,
                backEndCommission: salesPerson.backEndCommission || false,
                percentage: salesPerson.percentage || 0,
                flat: salesPerson.flat || 0,
                overRideAmount: salesPerson.overRideAmount || 0,
                actualAmount: salesPerson.actualAmount || 0,
                calculatedAmount: salesPerson.actualAmount || 0,
            })
        }
    }

}

